import React, { useState } from 'react';
import { FileText, X, ExternalLink, Loader2 } from 'lucide-react';
import config from '../../../config';

async function apiRequest(path, token) {
  const res = await fetch(`${config.baseURL}/api${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || body.message || `Request failed (${res.status})`);
  return body.data;
}

function formatUploadedAt(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Small icon button that, on click, loads and shows every report a specific
// patient has uploaded. Drop next to a patient's name anywhere in the
// dashboard — list rows, detail header, wherever.
export default function PatientReportsButton({ token, patientId, patientName }) {
  const [open, setOpen] = useState(false);
  const [reports, setReports] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function handleOpen(e) {
    e.stopPropagation(); // don't also trigger a parent row's onClick (e.g. selecting the appointment)
    setOpen(true);
    if (reports !== null) return; // already loaded once this mount

    setLoading(true);
    setError(null);
    apiRequest(`/doctor-portal/patients/${patientId}/reports`, token)
      .then(setReports)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  function handleClose(e) {
    e?.stopPropagation();
    setOpen(false);
  }

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label="View reports"
        title="View reports"
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: '#0F4C75',
          padding: 4,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <FileText size={16} />
      </button>

      {open && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 12,
              width: 380,
              maxWidth: '90vw',
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 16px',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div>
                <strong style={{ fontSize: 14 }}>Reports</strong>
                {patientName && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#888' }}>{patientName}</p>}
              </div>
              <button onClick={handleClose} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', padding: '4px 0' }}>
              {loading ? (
                <p style={{ padding: 20, fontSize: 13, color: '#666', textAlign: 'center' }}>
                  <Loader2 size={16} className="spin" style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Loading…
                </p>
              ) : error ? (
                <p style={{ padding: 20, fontSize: 13, color: '#B3261E' }}>{error}</p>
              ) : reports && reports.length === 0 ? (
                <p style={{ padding: 20, fontSize: 13, color: '#666' }}>No reports uploaded yet.</p>
              ) : (
                reports?.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 16px',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.original_name}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#999' }}>
                        {formatUploadedAt(r.uploaded_at)} · {formatFileSize(r.size_bytes)}
                      </p>
                    </div>
                    <a
                      href={`${config.baseURL}/uploads/${r.file_path}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Open report"
                      style={{ color: '#0F4C75', flexShrink: 0, display: 'flex' }}
                    >
                      <ExternalLink size={15} />
                    </a>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}